import React from "react";
import { Building2, FolderKanban, Users, Key, Webhook, TrendingUp } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, max, color = "teal" }) => {
    const percentage = max ? Math.min((value / max) * 100, 100) : 0;
    const colorClasses = {
        teal: "from-teal-400 to-teal-600 text-teal-400",
        blue: "from-blue-400 to-blue-600 text-blue-400",
        purple: "from-purple-400 to-purple-600 text-purple-400",
        amber: "from-amber-400 to-amber-600 text-amber-400",
        emerald: "from-emerald-400 to-emerald-600 text-emerald-400"
    };

    return (
        <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color].split(' ').slice(0, 2).join(' ')} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <span className={`text-2xl font-bold ${colorClasses[color].split(' ').pop()}`}>
                    {value}
                </span>
            </div>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-sm dark:text-slate-400 text-slate-600">{label}</span>
                    {max && (
                        <span className="text-xs dark:text-slate-500 text-slate-400">
                            / {max}
                        </span>
                    )}
                </div>
                {max && (
                    <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className={`h-full bg-gradient-to-r ${colorClasses[color].split(' ').slice(0, 2).join(' ')} transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const OrganizationOverview = ({ organization, workspaces, members, limits }) => {
    // Calculate stats
    const totalGroups = workspaces.reduce((acc, ws) => acc + (ws.groups?.length || 0), 0);
    const activeMembers = members.filter(m => m.status === 'Active').length;
    const pendingInvites = members.filter(m => m.status === 'Pending').length;

    return (
        <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                    icon={FolderKanban}
                    label="Workspaces"
                    value={workspaces.length}
                    max={limits.workspaces}
                    color="blue"
                />
                <StatCard
                    icon={Users}
                    label="Total Members"
                    value={members.length}
                    max={limits.members}
                    color="teal"
                />
                <StatCard
                    icon={Users}
                    label="Active Members"
                    value={activeMembers}
                    color="emerald"
                />
                <StatCard
                    icon={Users}
                    label="Pending Invites"
                    value={pendingInvites}
                    color="amber"
                />
            </div>

            {/* Organization Details */}
            <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-teal-500" />
                    Organization Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                            Name
                        </label>
                        <p className="dark:text-white text-slate-900 font-medium mt-1">
                            {organization?.name || "—"}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                            Created
                        </label>
                        <p className="dark:text-white text-slate-900 font-medium mt-1">
                            {organization?.created_at
                                ? new Date(organization.created_at).toLocaleDateString()
                                : "—"}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                            Organization ID
                        </label>
                        <p className="dark:text-slate-400 text-slate-600 font-mono text-sm mt-1">
                            {organization?.id || "—"}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                            Plan
                        </label>
                        <p className="dark:text-white text-slate-900 font-medium mt-1 capitalize">
                            {organization?.plan || "Ultra"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-teal-500" />
                    Quick Stats
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 dark:bg-slate-800/50 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold dark:text-white text-slate-900">{totalGroups}</p>
                        <p className="text-xs dark:text-slate-400 text-slate-500 mt-1">Total Groups</p>
                    </div>
                    <div className="text-center p-4 dark:bg-slate-800/50 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold dark:text-white text-slate-900">
                            {limits.workspaces - workspaces.length}
                        </p>
                        <p className="text-xs dark:text-slate-400 text-slate-500 mt-1">Available Workspaces</p>
                    </div>
                    <div className="text-center p-4 dark:bg-slate-800/50 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold dark:text-white text-slate-900">
                            {limits.members - members.length}
                        </p>
                        <p className="text-xs dark:text-slate-400 text-slate-500 mt-1">Available Seats</p>
                    </div>
                    <div className="text-center p-4 dark:bg-slate-800/50 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold dark:text-white text-slate-900">
                            {Math.round((activeMembers / (members.length || 1)) * 100)}%
                        </p>
                        <p className="text-xs dark:text-slate-400 text-slate-500 mt-1">Active Rate</p>
                    </div>
                </div>
            </div>

            {/* Recent Workspaces */}
            {workspaces.length > 0 && (
                <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                        <FolderKanban className="w-5 h-5 text-blue-500" />
                        Recent Workspaces
                    </h3>
                    <div className="space-y-2">
                        {workspaces.slice(0, 5).map((ws) => (
                            <div
                                key={ws.id}
                                className="flex items-center justify-between p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                                        {ws.name?.charAt(0)?.toUpperCase() || "W"}
                                    </div>
                                    <div>
                                        <p className="dark:text-white text-slate-900 font-medium">{ws.name}</p>
                                        <p className="text-xs dark:text-slate-400 text-slate-500">
                                            {ws.memberCount || 0} members
                                        </p>
                                    </div>
                                </div>
                                <span className="text-xs dark:text-slate-500 text-slate-400">
                                    {ws.groupCount || 0} groups
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrganizationOverview;
